import { Icon, Tag } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { formatMoneyWithCurrency } from '../../../utils'
import GridSquare from '../GridSquare'
import { ProductV4 } from '../../../types/api'
import { Help } from '../../Help'
import { UprightStyle } from '../../../types'
import './orderDetailsStyle.scss'
import { OrderDetailsTable } from './OrderDetailsTable'

const style: UprightStyle = {
  tag: { marginLeft: 3 },
  spacer: { margin: `15px 0 30px 0` },
  orderSumTotalHelper: { maxWidth: 200 },
  ordersAtAGlance: { color: '#9ca0a7' }
}

export const OrderDetails = ({ product }: { product: ProductV4 }) => {
  const orders = React.useMemo(() => {
    if (product.orders) return product.orders

    return !product.order ? [] : [product.order]
  }, [product])

  const ordersSumCurrencyCode = orders[0]?.currencyCode || 'USD' // We're going to ignore the possibility of multiple currencies for now

  const variants = React.useMemo(() => product.variants || [], [product.variants])
  const variantIds = React.useMemo(() => [product.id, ...variants.map(variant => variant.id)], [product.id, variants])

  const { productsTotalPaid, unfulfilledOrdersCount, productsOrderItemPaidMap } = React.useMemo(() => {
    const cache = {
      unfulfilledOrdersCount: 0,
      productsTotalPaid: 0.0,
      productsOrderItemPaidMap: {}
    }

    orders.forEach(order => {
      if (order?.fulfillmentStatus === 'UNFULFILLED' && order?.paymentStatus !== 'REFUNDED')
        cache.unfulfilledOrdersCount += 1
      const orderItemSum = order.orderItems
        ?.filter(oi => oi.cancelledAt === null)
        .reduce((acc, oi) => {
          let refundedAmount = 0.0
          if (oi?.refundItems?.length > 0) {
            refundedAmount = oi.refundItems.reduce((acc2, ri) => {
              return acc2 + ri.amount
            }, 0.0)
          }
          if (variantIds.includes(oi.productId)) {
            const itemSoldTotal = oi.price * oi.quantity
            return acc + itemSoldTotal - refundedAmount
          }
          return acc
        }, 0.0)

      cache.productsOrderItemPaidMap[order.id] = order?.paymentStatus !== 'REFUNDED' ? orderItemSum || 0.0 : 0.0
      cache.productsTotalPaid += order?.paymentStatus !== 'REFUNDED' ? orderItemSum || 0.0 : 0.0
    })
    return cache
  }, [orders, variantIds])

  return (
    <div className="order-details">
      <div className="drawer-header">
        <Icon icon={IconNames.SHOPPING_CART} /> Orders{' '}
        <Tag minimal round style={style.tag}>
          {orders.length}
        </Tag>
      </div>
      {!product.order && orders.length === 0 ? (
        <p className="non-ideal">No orders yet. Please check back later.</p>
      ) : (
        <>
          {orders.length >= 3 && (
            <div>
              <h4>Orders at a Glance</h4>
              <div className="details" style={style.ordersAtAGlance}>
                <GridSquare
                  title={
                    <>
                      Item Revenue{' '}
                      <Help>
                        <div style={style.orderSumTotalHelper}>
                          <span>
                            Item Revenue measures how much customers paid for the items in the orders below. This
                            excludes shipping, handling, taxes and any cancelled or refunded orders.
                          </span>
                        </div>
                      </Help>
                    </>
                  }
                  contents={formatMoneyWithCurrency(productsTotalPaid, ordersSumCurrencyCode)}
                />
                <GridSquare title="Unfulfilled Orders" contents={unfulfilledOrdersCount} />
              </div>
            </div>
          )}
          <div>
            <h4>All Orders</h4>
            <OrderDetailsTable
              orders={orders}
              variants={variants}
              productCarrierName={product?.carrier?.name}
              productsOrderItemPaidMap={productsOrderItemPaidMap}
            />
          </div>
        </>
      )}
    </div>
  )
}
